import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';

const fromRuntime =
  typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined;

const sentryDsn = (
  fromRuntime?.REACT_APP_SENTRY_DSN ?? process.env.REACT_APP_SENTRY_DSN ?? ''
).trim();

const sentryEnvironment =
  fromRuntime?.REACT_APP_SENTRY_ENVIRONMENT ??
  process.env.REACT_APP_SENTRY_ENVIRONMENT ??
  process.env.NODE_ENV ??
  'development';

export const isSentryEnabled = Boolean(sentryDsn);
export const sentryEnvironmentName = sentryEnvironment;

if (isSentryEnabled) {
  const isDevEnvironment =
    sentryEnvironment === 'dev' ||
    sentryEnvironment === 'development' ||
    process.env.NODE_ENV === 'development';

  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications'
    ],
    dataCollection: {
      // To disable sending user data and HTTP bodies, uncomment the lines below.
      // userInfo: false,
      // httpBodies: []
    },
    integrations: [Sentry.replayIntegration()],
    replaysSessionSampleRate: isDevEnvironment ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0
  });

  applyStoredSentryUser();
}

export function setSentryUser(user: { id: number; login: string; role?: string } | null): void {
  if (!isSentryEnabled) {
    return;
  }

  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: String(user.id),
    username: user.login,
    segment: user.role
  });
}

export function captureApiError(error: unknown): void {
  if (!isSentryEnabled) {
    return;
  }

  if (error instanceof AxiosError) {
    Sentry.captureException(error, {
      extra: {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        errorId: error.response?.data?.errorId
      }
    });
    return;
  }

  Sentry.captureException(error);
}

function applyStoredSentryUser(): void {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) {
      return;
    }

    const user = JSON.parse(raw) as { id?: number; login?: string; role?: string };
    if (user?.id && user.login) {
      setSentryUser({ id: user.id, login: user.login, role: user.role });
    }
  } catch {
    // ignore malformed stored user
  }
}
